import moment from "moment-mini";
import type { EventProps, HedgedPositionProps, HedgeProps, MarketProps, MatchProps, OrderProps, OrderStatsProps, PositionProps, TournamentProps } from "../../../../types";
import { MarketComponentHelpers } from "../../../api";


export const MyOrdersHelpers = {
    getContestsFromOrders: (orders:OrderProps[]) => {
        let event_ids = [ ...new Set(orders.filter(o => o.event_type == 'team').map(o => o.event_id))]
        let tournament_ids = [ ...new Set(orders.filter(o => o.event_type == 'tournament').map(o => o.event_id))]
        let match_ids = [ ...new Set(orders.filter(o => o.event_type == 'match').map(o => o.event_id))]
        let athlete_ids = [ ...new Set(orders.filter(o => o.side_type == 'athlete').map(o => o.side_id))]
        let team_ids = [ ...new Set(orders.filter(o => o.side_type == 'team').map(o => o.side_id)) ]
        let player_ids = [ ...new Set(orders.map(o => o.player_id)) ]
        return { event_ids, tournament_ids, match_ids, athlete_ids, team_ids, player_ids }
    },
    getHedgesFromPositions: (orders:OrderProps[], markets:MarketProps[]):HedgeProps[] => {
        //1) Set up arrays to store hedges
        let hedges:HedgeProps[] = []
        let hedgeable_positions:Array<OrderProps[]> = []
        //Only allow real money orders for hedging
        orders = orders.filter(o => o.market_type === 'FOR_MONEY' && o.resolution_status !== 'closed')
    
        //2) Get the unique markets (and common var_1s) that are eligible to be hedged 
        let orders_with_positions = orders.filter(o => o.positions[0])
        let unique_markets = [ ...new Set(orders_with_positions.map(o => `${o.event_id}:${o.event_type}:${o.market_id}:${Math.abs(o.var_1)}`)) ]
       
        //3) For each unique market and var_1 combo, determin if there are positions on the opposite side of each other
        //If so, then store them into an array of orders
        unique_markets.map(id => {
            //1) Get orders that are associated with the market
            let market_orders = orders_with_positions.filter(o => `${o.event_id}:${o.event_type}:${o.market_id}:${Math.abs(o.var_1)}` == id)
            if(market_orders.length < 2){ return } //Impossible to hedge with less than 2 orders
            let unique_sides = [ ...new Set(market_orders.map(o => o.side)) ]
            if(unique_sides.length < 0){ return } //Must have more than 1 side
    
            hedgeable_positions.push(market_orders)
        })
    
        //Return nothing if there are no hedgeable positions
        if(hedgeable_positions.length === 0){ return [] }
    
        //Awesome! We have some hedgeable positions
        hedgeable_positions.map(hp => {
            //Get an empty hedge object to store our hedges
            let hedge = MyOrdersHelpers.getEmptyHedge()
            let market = markets.find(m => m.market_id == hp[0]?.market_id)
            if(!market){ return } //Error out if there is no market
            if(market.level !== 'event'){ return } //Dont if level is not event
    
            let trade_side_orders = hp.filter(o => o.side == market?.trade_side)
            let o_side_orders = hp.filter(o => o.side != market?.trade_side)
            
            //New
            if(market.type == 'Spread'){
                let this_order = trade_side_orders[0]
                if(!this_order){ return } //Make sure there is at least 1 trade side order
                o_side_orders = o_side_orders.filter(o => o.var_1 == this_order.var_1 * -1)
            }
    
            if(trade_side_orders.length === 0 || o_side_orders.length === 0){ return }
    
            if(!hp[0]){ return }
    
    
            hedge.market_id = market.market_id
            hedge.market_type = hp[0].market_type
            hedge.var_1 = Math.abs(hp[0].var_1)
            hedge.player_id = hp[0].player_id
            hedge.event_id = hp[0].event_id
            hedge.event_type = hp[0].event_type
            hedge.market_id = hp[0].market_id
           
    
    
            //Set up arrays to store the positions on each side of the hedge
            let trade_side_positions:PositionProps[] = []
            let o_side_positions:PositionProps[] = [] 
            trade_side_orders.map(o => {
                trade_side_positions = trade_side_positions.concat(o.positions)
            })
            o_side_orders.map(o => {
                o_side_positions = o_side_positions.concat(o.positions)
            })
    
            //Calculate the winnings from each outcome (side a wins, side b wins, or draw)
            let trade_side_pot_winnings = trade_side_positions.reduce((a,b) => a + b.potential_winnings, 0)
            let o_side_pot_winnings = o_side_positions.reduce((a,b) => a + b.potential_winnings, 0)
            let draw_winnings = trade_side_positions.concat(o_side_positions).reduce((a,b) => a + b.stake, 0)
    
            //Get the minimum payout possible of the 3 outcomes which is the hedged amount
            let hedged_amt = Math.min(trade_side_pot_winnings, o_side_pot_winnings)
            if(isNaN(hedged_amt)){ return }
            hedge.hedged_amt = hedged_amt
    
            //Now lets cals out hedged positions
            let hedged_positions:HedgedPositionProps[] = []
            
            trade_side_positions.map(p => {
                //Need to determine the weight of the position on the total potential winnings and reduce the stake proportionately
                let pct = p.potential_winnings / trade_side_pot_winnings
                //console.log(hedged_amt, pct, p.probability)
                let stake_from_hedge = hedged_amt * pct * p.probability
                hedge.stake_reduction += stake_from_hedge 
                let new_stake = p.stake - stake_from_hedge
                hedged_positions.push({
                    hedge_id:'',
                    hedged_position_id: '',
                    position_id: p.position_id,
                    original_stake: p.stake,
                    new_stake,
                    delayed_cash_draw: 0,
                    delayed_cash_no_draw: 0,
                    original_potential_winnings: p.potential_winnings,
                    new_potential_winnings: new_stake / p.probability,
                    status: 'pending',
                    create_datetime:'',
                    last_update_datetime:''
                })
            })
    
            o_side_positions.map(p => {
                let pct = p.potential_winnings / o_side_pot_winnings
                let stake_from_hedge = hedged_amt * pct * p.probability
                hedge.stake_reduction += stake_from_hedge 
                let new_stake = p.stake - stake_from_hedge
                hedged_positions.push({
                    hedge_id:'',
                    hedged_position_id: '',
                    position_id: p.position_id,
                    original_stake: p.stake,
                    new_stake,
                    delayed_cash_draw: 0,
                    delayed_cash_no_draw: 0,
                    original_potential_winnings: p.potential_winnings,
                    new_potential_winnings: new_stake / p.probability,
                    status: 'pending',
                    create_datetime:'',
                    last_update_datetime:''
                })
            })
    
    
            //Cash rcvd will be the lower of the hedged amount or the reduction in stake
            let cash_rcvd = Math.min(hedge.stake_reduction, hedged_amt, draw_winnings)
            if(isNaN(cash_rcvd) || cash_rcvd < 0.01){ return }
            hedge.cash_rcvd = cash_rcvd
            let delayed_cash = hedged_amt - hedge.stake_reduction
            let total_og_stake = hedged_positions.reduce((a,b) => a + b.original_stake, 0)
            let delayed_cash_draw = 0, delayed_cash_no_draw = 0;
            if(delayed_cash < 0){
                delayed_cash_draw = delayed_cash * -1
                //hedge.delayed_cash_draw = delayed_cash * -1 //Need to make positive
            } else {
                delayed_cash_no_draw = delayed_cash
                //hedge.delayed_cash_no_draw = delayed_cash
            }
            hedge.delayed_cash_draw = delayed_cash_draw
            hedge.delayed_cash_no_draw = delayed_cash_no_draw
    
            hedged_positions.map(hp => {
                let pct_of_total = hp.original_stake / total_og_stake
                hp.delayed_cash_draw = pct_of_total * delayed_cash_draw
                hp.delayed_cash_no_draw = pct_of_total * delayed_cash_no_draw
                return hp
            })
    
            hedges.push({ ...hedge, hedged_positions:hedged_positions })
        })
    
        return hedges
    },
    getEmptyHedge: ():HedgeProps => {
        let uuid = Math.floor(Math.random() * 100000000)
        return {
            hedge_id: uuid.toString(),
            hedged_amt: 0,
            event_id: '',
            event_type: '',
            market_id: '',
            var_1: 0,
            delayed_cash_draw: 0,
            delayed_cash_no_draw: 0,
            cash_rcvd: 0,
            stake_reduction: 0,
            market_type: 'FOR_MONEY',
            create_datetime: '',
            last_udpate_datetime:'',
            player_id: '',
            status: 'pending'
        }
    },
    getDetailsForStat: (order_stat:OrderStatsProps, events:EventProps[], tournaments:TournamentProps[], matches:MatchProps[]):{ contest_title:string, contest_time_detail:string, league_id:string } => {
        let fail_response = { contest_title: 'TBD', contest_time_detail: 'TBD', league_id:'' }
        switch(order_stat.event_type){
            case 'team':
                let event = events.find(e => e.event_id == order_stat.event_id);
                if(!event){ return fail_response }
                return {
                    league_id: event.league_id,
                    contest_title: `${event.away?.abbr} ${event.away_team_score} - ${event.home?.abbr} ${event.home_team_score}`,
                    contest_time_detail: event.time_detail && event.time_detail != 'scheduled' ? event.time_detail : moment(event.scheduled_datetime).format('MM/DD @ hh:mm a')
                }
            case 'tournament':
                let tournament = tournaments.find(t => t.tournament_id == order_stat.event_id)
                if(!tournament) { return fail_response }
                return {
                    league_id: tournament.league_id,
                    contest_title: tournament.tournament_name,
                    contest_time_detail: moment(tournament.scheduled_datetime).format('MM/DD/YYYY')
                }
            case 'match':
                let match = matches.find(m => m.match_id == order_stat.event_id)
                if(!match){ return fail_response }
                let match_tournament = tournaments.find(t => t.tournament_id == match.tournament_id);
                return {
                    league_id: match_tournament?.league_id ?? '',
                    contest_title: match.match_title,
                    contest_time_detail: match.time_detail && match.time_detail != 'scheduled' ? match.time_detail : moment(match.scheduled_datetime).format('MM/DD @ hh:mm a')
                }
            default:
                return fail_response
        }
    },
   calcOrderStats: (orders:OrderProps[], player_id:string):OrderStatsProps[] => {
    let order_stats:OrderStatsProps[] = []
    let unique_headers = [ ...new Set(orders.map(o => `${o.event_id}:${o.event_type}`)) ]
    let wins = 0, losses = 0, draws = 0;
    unique_headers.map(h => {
        let contest_id = h.split(':')[0]
        let contest_type = h.split(':')[1]
        if(!contest_id || !contest_type){ return }
        let found_orders = orders.filter(o => `${o.event_id}:${o.event_type}` === h)
        if(found_orders.length === 0){ return; }
        let max_last_update = found_orders.filter(o => o.status == 'approved').find(o => Math.min(moment(o.expire_datetime).unix()))?.expire_datetime
        if(!max_last_update){ max_last_update = moment().add(1, 'year') }
        let unique_order_titles = [ ...new Set(found_orders.map(o => o.title)) ]
        let open_order_count = 0, open_amt = 0, potential_winnings = 0, called_amt = 0, commission = 0;
        let resolved_stake = 0, original_stake = 0, delayed_cash = 0, winnings = 0, net_winnings = 0, unresolved_stake = 0, resolved_potential_winnings = 0, unresolved_potential_net_winnings = 0, unresolved_potential_winnings = 0, num_trades = 0, cash_rcvd = 0; 
        let result_ind:string|undefined = undefined
        let order_ids:string[] = []
        let open_orders:string[] = []
        found_orders.map(o => {
            let commission_pct = 0
            if(o.commission_pct){ commission_pct = o.commission_pct }
            order_ids.push(o.order_id)
            if(o.status === 'approved'){
                open_orders.push(o.order_id)
                open_amt += o.open_amt
                open_order_count += 1
                potential_winnings += o.potential_winnings
                called_amt += o.called_amt
            }
            if(!o.positions){ return }
            o.positions.map(p => {
                if(!p){ return }
                num_trades += 1
                cash_rcvd += p.cash_rcvd
                if(!p.result_ind){
                    delayed_cash += p.delayed_cash
                    unresolved_stake += p.stake
                    unresolved_potential_winnings += p.potential_winnings
                    unresolved_potential_net_winnings += (p.potential_winnings - ((p.potential_winnings - p.stake) * commission_pct))
                } else {
                    resolved_stake += p.stake
                    original_stake += p.original_stake
                    winnings += p.winnings
                    net_winnings += p.net_winnings
                    result_ind = p.result_ind
                    commission += p.commission
                    resolved_potential_winnings += p.potential_winnings
                }
            })
        })
        if(result_ind == 'win'){ wins += 1 }
        if(result_ind == 'lose'){ losses += 1 }
        if(result_ind == 'draw'){ draws += 1 }
        let unresolved_position_probability = unresolved_stake / unresolved_potential_winnings
        let resolved_position_probability = resolved_stake / resolved_potential_winnings
        let unresolved_position_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(unresolved_position_probability)
        let resolved_position_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(resolved_position_probability)

        let offer_probability = open_amt / potential_winnings
        let offer_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(offer_probability)
        let earnings = winnings + cash_rcvd - original_stake
        let net_earnings = net_winnings + cash_rcvd - original_stake

        let roi = earnings / original_stake
        return order_stats.push({
            stat_id: h,
            unresolved_stake,
            earnings,
            resolved_stake,
            unique_order_titles,
            original_stake,
            roi,
            net_earnings,
            wins,
            net_winnings,
            commission,
            unresolved_potential_net_winnings,
            losses,
            draws,
            unresolved_position_odds,
            resolved_position_odds,
            result_ind,
            delayed_cash,
            unresolved_potential_winnings,
            resolved_potential_winnings,
            resolved_position_probability,
            unresolved_position_probability,
            num_trades,
            cash_rcvd,
            winnings,
            player_id,
            event_id: contest_id,
            event_type: contest_type,
            open_amt, potential_winnings, called_amt, offer_odds, offer_probability,
            orders: order_ids,
            open_orders,
            last_update_datetime: max_last_update
        })
    })
    return order_stats
   },
   calcAllOrderStats: (orders:OrderProps[]):OrderStatsProps => {
    let open_order_count = 0, open_amt = 0, potential_winnings = 0, called_amt = 0, commission = 0;
    let resolved_stake = 0, original_stake = 0, delayed_cash = 0, winnings = 0, net_winnings = 0, unresolved_stake = 0, resolved_potential_winnings = 0, unresolved_potential_winnings = 0, unresolved_potential_net_winnings = 0, num_trades = 0, cash_rcvd = 0; 
    let unique_order_titles = [ ...new Set(orders.map(o => o.title)) ]
    let result_ind:string|undefined = undefined
    let order_ids:string[] = []
    let open_orders:string[] = []
    let wins = 0, losses = 0, draws = 0;
    orders.map(o => {
        let commission_pct = 0
        if(o.commission_pct){ commission_pct = o.commission_pct }
        order_ids.push(o.order_id)
        if(o.status === 'approved'){
            open_orders.push(o.order_id)
            open_amt += o.open_amt
            open_order_count += 1
            potential_winnings += o.potential_winnings
            called_amt += o.called_amt
        }
        if(!o.positions){ return }
        o.positions.map(p => {
            if(!p){ return }
            num_trades += 1
            cash_rcvd += p.cash_rcvd
            if(!p.result_ind){
                delayed_cash += p.delayed_cash
                unresolved_stake += p.stake
                unresolved_potential_winnings += p.potential_winnings
                unresolved_potential_net_winnings += (p.potential_winnings - ((p.potential_winnings - p.stake) * commission_pct))

            } else {
                resolved_stake += p.stake
                original_stake += p.original_stake
                winnings += p.winnings
                net_winnings += p.net_winnings
                result_ind = p.result_ind
                commission += p.commission
                resolved_potential_winnings += p.potential_winnings
            }
        })
        if(result_ind == 'win'){ wins += 1 }
        if(result_ind == 'lose'){ losses += 1 }
        if(result_ind == 'draw'){ draws += 1 }
    })
   
    let unresolved_position_probability = unresolved_stake / unresolved_potential_winnings
    let resolved_position_probability = resolved_stake / resolved_potential_winnings
    let unresolved_position_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(unresolved_position_probability)
    let resolved_position_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(resolved_position_probability)

    let offer_probability = open_amt / potential_winnings
    let offer_odds = MarketComponentHelpers.calcAmericanOddsFromProbability(offer_probability)
    let earnings = winnings + cash_rcvd - original_stake
    let net_earnings = net_winnings + cash_rcvd - original_stake
    let roi = earnings / resolved_stake
    return {
        stat_id: 'ALL',
        unresolved_stake,
        earnings,
        resolved_stake,
        original_stake,
        roi,
        wins,
        losses,
        commission,
        net_winnings,
        net_earnings,
        unresolved_potential_net_winnings,
        unique_order_titles,
        draws,
        unresolved_position_odds,
        resolved_position_odds,
        result_ind,
        delayed_cash,
        unresolved_potential_winnings,
        resolved_potential_winnings,
        resolved_position_probability,
        unresolved_position_probability,
        num_trades,
        cash_rcvd,
        winnings,
        player_id: orders[0]?.player_id??'0',
        event_id: 'ALL',
        event_type: 'ALL',
        open_amt, potential_winnings, called_amt, offer_odds, offer_probability,
        orders: order_ids,
        open_orders
    }
}
}